import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '../components/ui/alert-dialog';
import { 
  Users, 
  Plus, 
  Edit2, 
  Trash2, 
  Copy, 
  Check,
  Shield,
  UserCircle,
  Phone,
  Mail,
  Link2
} from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const roleConfig = {
  parent: { label: 'Parent', color: 'bg-nurture-navy text-white', description: 'Full access to all features' },
  partner: { label: 'Partner', color: 'bg-teal-500 text-white', description: 'Full access to all features' },
  asisten: { label: 'Asisten', color: 'bg-amber-500 text-white', description: 'Today\'s tasks and handoff only' },
  grandparent: { label: 'Grandparent', color: 'bg-nurture-sage text-white', description: 'Read-only access to tasks' },
  driver: { label: 'Driver', color: 'bg-gray-500 text-white', description: 'Pickup/dropoff tasks only' },
  inlaw: { label: 'In-Law', color: 'bg-purple-400 text-white', description: 'Read-only access to tasks' },
};

const accessLevelConfig = {
  full: { label: 'Full Access', description: 'Can view and edit everything' },
  limited: { label: 'Limited', description: 'Can view and edit assigned items' },
  readonly: { label: 'Read Only', description: 'Can only view, no editing' },
  tasks_only: { label: 'Tasks Only', description: 'Can only see assigned tasks' },
};

const FamilyRolesPage = () => {
  const { user } = useAuth();
  const [roleData, setRoleData] = useState({ family_id: null, members: [] });
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingMember, setEditingMember] = useState(null);
  const [copiedLink, setCopiedLink] = useState(null);
  
  const [newMember, setNewMember] = useState({
    name: '',
    role: 'asisten',
    whatsapp_number: '',
    access_level: 'limited',
    email: '',
    password: ''
  });

  useEffect(() => {
    fetchRoles();
  }, []);

  const fetchRoles = async () => {
    try {
      const response = await axios.get(`${API}/family/roles`);
      setRoleData(response.data);
    } catch (error) {
      console.error('Error fetching roles:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/family/roles/member`, newMember);
      toast.success(`${newMember.name} added as ${roleConfig[newMember.role].label}!`);
      setDialogOpen(false);
      setNewMember({ name: '', role: 'asisten', whatsapp_number: '', access_level: 'limited', email: '', password: '' });
      fetchRoles();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add member');
    }
  };

  const handleUpdate = async () => {
    if (!editingMember) return;
    try {
      await axios.put(`${API}/family/roles/member/${editingMember.member_id}`, {
        name: editingMember.name,
        whatsapp_number: editingMember.whatsapp_number
      });
      toast.success('Member updated!');
      setEditingMember(null);
      fetchRoles();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update member');
    }
  };

  const handleDelete = async (memberId, memberName) => {
    try {
      await axios.delete(`${API}/family/roles/member/${memberId}`);
      toast.success(`${memberName} removed from family`);
      fetchRoles();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to remove member');
    }
  };

  const copyInviteLink = (member) => {
    // Generate a shareable link (UI only - no actual invite system yet)
    const inviteLink = `${window.location.origin}/invite/${member.member_id}`;
    navigator.clipboard.writeText(inviteLink);
    setCopiedLink(member.member_id);
    toast.success('Invite link copied!');
    setTimeout(() => setCopiedLink(null), 3000);
  };

  const isCurrentUser = (member) => member.member_id === user?.id;

  return (
    <div className="space-y-6" data-testid="family-roles-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-heading font-bold text-nurture-navy flex items-center gap-3">
            <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-2xl bg-purple-100 flex items-center justify-center">
              <Users className="w-5 h-5 sm:w-6 sm:h-6 text-purple-600" />
            </div>
            Family Roles
          </h1>
          <p className="text-nurture-muted mt-1">Who has access to NurtureOS and what they can see</p>
        </div>
        
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button 
              className="bg-nurture-sage hover:bg-nurture-sage-hover text-white rounded-xl shadow-nurture-button"
              data-testid="add-member-btn"
            >
              <Plus className="w-5 h-5 mr-2" />
              Add Member
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>Add Family Member</DialogTitle>
              <DialogDescription>
                Add someone to your family and set their access level.
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label>Name *</Label>
                <Input
                  value={newMember.name}
                  onChange={(e) => setNewMember({...newMember, name: e.target.value})}
                  placeholder="Enter name"
                  required
                  data-testid="member-name-input"
                />
              </div>
              
              <div className="space-y-2">
                <Label>Role *</Label>
                <Select 
                  value={newMember.role} 
                  onValueChange={(v) => setNewMember({...newMember, role: v})}
                >
                  <SelectTrigger data-testid="member-role-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(roleConfig).map(([key, config]) => (
                      <SelectItem key={key} value={key}>
                        <div className="flex items-center gap-2">
                          <Badge className={`${config.color} text-xs`}>{config.label}</Badge>
                          <span className="text-xs text-nurture-muted">{config.description}</span>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-2">
                <Label>WhatsApp Number (optional)</Label>
                <Input
                  value={newMember.whatsapp_number}
                  onChange={(e) => setNewMember({...newMember, whatsapp_number: e.target.value})}
                  placeholder="+62 812 3456 7890"
                  data-testid="member-whatsapp-input"
                />
              </div>
              
              <div className="border-t pt-4 space-y-2">
                <Label className="flex items-center gap-2">
                  <Mail className="w-4 h-4" />
                  Login Credentials (optional)
                </Label>
                <p className="text-xs text-nurture-muted">
                  If you want this person to log in themselves, add their email and password.
                </p>
                <Input
                  type="email"
                  value={newMember.email}
                  onChange={(e) => setNewMember({...newMember, email: e.target.value})}
                  placeholder="Email address"
                  data-testid="member-email-input"
                />
                <Input
                  type="password"
                  value={newMember.password}
                  onChange={(e) => setNewMember({...newMember, password: e.target.value})}
                  placeholder="Password"
                  data-testid="member-password-input"
                />
              </div>
              
              <Button 
                type="submit" 
                className="w-full bg-nurture-sage hover:bg-nurture-sage-hover"
                data-testid="submit-member-btn"
              >
                Add Member
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Role Legend */}
      <Card className="bg-gradient-to-r from-purple-50 to-indigo-50 border-none">
        <CardContent className="p-4">
          <div className="flex items-center gap-2 mb-3">
            <Shield className="w-5 h-5 text-purple-600" />
            <span className="font-medium text-nurture-navy">Access Levels</span>
          </div>
          <div className="flex flex-wrap gap-2">
            {Object.entries(roleConfig).map(([key, config]) => (
              <div key={key} className="flex items-center gap-2">
                <Badge className={`${config.color} text-xs`}>{config.label}</Badge>
                <span className="text-xs text-nurture-muted hidden sm:inline">{config.description}</span>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Members List */}
      {loading ? (
        <div className="space-y-4">
          {[1, 2, 3].map(i => (
            <div key={i} className="h-24 bg-nurture-cream-dark rounded-2xl animate-pulse" />
          ))}
        </div>
      ) : (
        <div className="space-y-4">
          <AnimatePresence>
            {roleData.members.map((member, index) => (
              <motion.div
                key={member.member_id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ delay: index * 0.05 }}
              >
                <Card className="shadow-nurture">
                  <CardContent className="p-4 sm:p-6">
                    <div className="flex flex-col sm:flex-row sm:items-center gap-4">
                      {/* Avatar and Name */}
                      <div className="flex items-center gap-4 flex-1">
                        <div className={`w-12 h-12 rounded-full flex items-center justify-center ${roleConfig[member.role]?.color || 'bg-gray-200'}`}>
                          <UserCircle className="w-6 h-6" />
                        </div>
                        <div className="flex-1 min-w-0">
                          {editingMember?.member_id === member.member_id ? (
                            <Input
                              value={editingMember.name}
                              onChange={(e) => setEditingMember({...editingMember, name: e.target.value})}
                              className="font-medium"
                              data-testid={`edit-name-${member.member_id}`}
                            />
                          ) : (
                            <div className="flex items-center gap-2">
                              <p className="font-medium text-nurture-navy truncate">{member.name}</p>
                              {isCurrentUser(member) && (
                                <Badge variant="outline" className="text-xs">You</Badge>
                              )}
                            </div>
                          )}
                          <div className="flex items-center gap-2 mt-1">
                            <Badge className={`${roleConfig[member.role]?.color || 'bg-gray-200'} text-xs`}>
                              {roleConfig[member.role]?.label || member.role}
                            </Badge>
                            {member.email && (
                              <span className="text-xs text-nurture-muted truncate hidden sm:inline">
                                {member.email}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                      
                      {/* WhatsApp */}
                      <div className="flex items-center gap-2 sm:w-48">
                        <Phone className="w-4 h-4 text-nurture-muted" />
                        {editingMember?.member_id === member.member_id ? (
                          <Input
                            value={editingMember.whatsapp_number || ''}
                            onChange={(e) => setEditingMember({...editingMember, whatsapp_number: e.target.value})}
                            placeholder="+62..."
                            className="text-sm"
                            data-testid={`edit-phone-${member.member_id}`}
                          />
                        ) : (
                          <span className="text-sm text-nurture-muted">
                            {member.whatsapp_number || 'Not set'}
                          </span>
                        )}
                      </div>
                      
                      {/* Actions */}
                      <div className="flex items-center gap-2 justify-end">
                        {editingMember?.member_id === member.member_id ? (
                          <>
                            <Button
                              size="sm"
                              onClick={handleUpdate}
                              className="bg-nurture-sage hover:bg-nurture-sage-hover"
                              data-testid={`save-edit-${member.member_id}`}
                            >
                              <Check className="w-4 h-4" />
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => setEditingMember(null)}
                            >
                              Cancel
                            </Button>
                          </>
                        ) : (
                          <>
                            {/* Copy Invite Link */}
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => copyInviteLink(member)}
                              className="text-nurture-muted"
                              data-testid={`copy-link-${member.member_id}`}
                            >
                              {copiedLink === member.member_id ? (
                                <Check className="w-4 h-4 text-nurture-sage" />
                              ) : (
                                <Link2 className="w-4 h-4" />
                              )}
                            </Button>
                            
                            {/* Edit */}
                            {!isCurrentUser(member) && (
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => setEditingMember({...member})}
                                className="text-nurture-muted"
                                data-testid={`edit-member-${member.member_id}`}
                              >
                                <Edit2 className="w-4 h-4" />
                              </Button>
                            )}
                            
                            {/* Delete */}
                            {!isCurrentUser(member) && (
                              <AlertDialog>
                                <AlertDialogTrigger asChild>
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    className="text-red-500 hover:text-red-600 hover:border-red-300"
                                    data-testid={`delete-member-${member.member_id}`}
                                  >
                                    <Trash2 className="w-4 h-4" />
                                  </Button>
                                </AlertDialogTrigger>
                                <AlertDialogContent>
                                  <AlertDialogHeader>
                                    <AlertDialogTitle>Remove {member.name}?</AlertDialogTitle>
                                    <AlertDialogDescription>
                                      This will remove {member.name}'s access to NurtureOS. 
                                      They will no longer be able to log in or view family data.
                                    </AlertDialogDescription>
                                  </AlertDialogHeader>
                                  <AlertDialogFooter>
                                    <AlertDialogCancel>Cancel</AlertDialogCancel>
                                    <AlertDialogAction
                                      onClick={() => handleDelete(member.member_id, member.name)}
                                      className="bg-red-500 hover:bg-red-600"
                                    >
                                      Remove
                                    </AlertDialogAction>
                                  </AlertDialogFooter>
                                </AlertDialogContent>
                              </AlertDialog>
                            )}
                          </>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </AnimatePresence>
          
          {roleData.members.length === 0 && (
            <Card className="shadow-nurture">
              <CardContent className="py-12 text-center">
                <Users className="w-12 h-12 mx-auto text-nurture-muted/30 mb-4" />
                <p className="text-nurture-muted">No family members added yet</p>
                <p className="text-sm text-nurture-muted mt-1">Click "Add Member" to invite helpers, grandparents, or partners</p>
              </CardContent>
            </Card>
          )}
        </div>
      )}

      {/* Invite Section */}
      <Card className="bg-nurture-cream-dark/50 border-dashed">
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <Link2 className="w-5 h-5 text-nurture-terracotta" />
            Invite Links
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-nurture-muted mb-4">
            Share these links with family members to give them access. Each link is unique per member.
          </p>
          <div className="space-y-2">
            {roleData.members.filter(m => !isCurrentUser(m)).map(member => (
              <div key={member.member_id} className="flex items-center justify-between p-3 bg-white rounded-lg">
                <span className="text-sm">
                  Share this link with <strong>{member.name}</strong> to give them access
                </span>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => copyInviteLink(member)}
                >
                  {copiedLink === member.member_id ? (
                    <>
                      <Check className="w-4 h-4 mr-1 text-nurture-sage" />
                      Copied!
                    </>
                  ) : (
                    <>
                      <Copy className="w-4 h-4 mr-1" />
                      Copy Link
                    </>
                  )}
                </Button>
              </div>
            ))}
            {roleData.members.filter(m => !isCurrentUser(m)).length === 0 && (
              <p className="text-sm text-nurture-muted text-center py-4">
                Add members above to generate invite links
              </p>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default FamilyRolesPage;
